import React from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface CertificationCardProps {
  title: string;
  issuer: string;
  issueDate: string;
  expiryDate?: string;
  credentialId: string;
  verificationUrl: string;
  status: 'active' | 'expired' | 'pending';
  image?: string;
}

const CertificationCard: React.FC<CertificationCardProps> = ({
  title,
  issuer,
  issueDate,
  expiryDate,
  credentialId,
  verificationUrl,
  status,
  image,
}) => {
  const getStatusColor = () => {
    switch (status) {
      case 'active':
        return 'bg-success/10 text-success border-success/20';
      case 'expired':
        return 'bg-error/10 text-error border-error/20';
      case 'pending':
        return 'bg-warning/10 text-warning border-warning/20';
      default:
        return 'bg-muted text-text-secondary border-border';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'expired':
        return 'Expired';
      case 'pending':
        return 'Pending Renewal';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border hover:shadow-interactive transition-all duration-300 overflow-hidden">
      {/* Certificate Image Placeholder */}
      <div className="w-full h-48 bg-muted flex items-center justify-center border-b border-border">
        {image ? (
          <AppImage
            src={image}
            alt={`${title} certificate`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="flex flex-col items-center justify-center text-text-secondary">
            <Icon name="DocumentTextIcon" size={48} className="mb-2 opacity-40" />
            <span className="text-sm">Certificate Image</span>
          </div>
        )}
      </div>

      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="font-heading font-bold text-lg text-primary mb-1">
              {title}
            </h3>
            <p className="text-text-secondary text-sm">{issuer}</p>
          </div>
          <span
            className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor()}`}
          >
            {getStatusText()}
          </span>
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex items-center space-x-2 text-sm text-text-secondary">
            <Icon name="CalendarIcon" size={16} />
            <span>Issued: {issueDate}</span>
          </div>
          {expiryDate && (
            <div className="flex items-center space-x-2 text-sm text-text-secondary">
              <Icon name="ClockIcon" size={16} />
              <span>Expires: {expiryDate}</span>
            </div>
          )}
          <div className="flex items-center space-x-2 text-sm text-text-secondary">
            <Icon name="IdentificationIcon" size={16} />
            <span className="font-mono text-xs">{credentialId}</span>
          </div>
        </div>

        <a
          href={verificationUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center space-x-2 text-sm text-accent hover:text-accent/80 font-semibold transition-colors duration-200"
        >
          <Icon name="CheckBadgeIcon" size={16} />
          <span>Verify Credential</span>
          <Icon name="ArrowTopRightOnSquareIcon" size={14} />
        </a>
      </div>
    </div>
  );
};

export default CertificationCard;